import { createAsyncThunk } from "@reduxjs/toolkit";

import { MeasurementType } from "@fitness-app/data-models/entities/Measurement";
import { getErrorMessage } from "@fitness-app/utils";

import { type AsyncThunkCreator } from "../../../index";
import { type BodyPhotoRemovalPlan } from "../planBodyPhotoRemoval";
import { TRAINEE_MEASUREMENTS_REDUCER_NAME } from "../types";
import { addBodyPhotos } from "./addBodyPhotos";
import { deleteMeasurement } from "./deleteMeasurement";
import { fetchTraineeBodyPhotos } from "./fetchTraineeBodyPhotos";

type Payload = {
  traineeId: string;
  userId?: string | null;
  /**
   * Either a plan over the loaded rows (single photos) or a whole day, which is resolved against the
   * database so sets of that day beyond the loaded pages are deleted too.
   */
  removal: { plan: BodyPhotoRemovalPlan } | { eventDate: string };
};

/**
 * TRE-2109 — sets that keep photos are rewritten with the smaller `content` (the update webhook keeps
 * the activity entry in sync and drops the files), sets left empty are deleted (the delete webhook
 * removes the entry and the files). Deleting rows shifts the server-side pages, so the pages loaded
 * so far are fetched again afterwards instead of being patched in place.
 */
export const removeBodyPhotos = createAsyncThunk<void, Payload, AsyncThunkCreator<string>>(
  `${TRAINEE_MEASUREMENTS_REDUCER_NAME}/removeBodyPhotos`,
  async ({ traineeId, userId, removal }, { dispatch, getState, rejectWithValue, extra: { db, analytics } }) => {
    try {
      let plan: BodyPhotoRemovalPlan;
      if ("plan" in removal) {
        plan = removal.plan;
      } else {
        const { data, error } = await db
          .from("measurement")
          .select("id")
          .eq("traineeId", traineeId)
          .eq("type", MeasurementType.ClientPhoto)
          .eq("eventDate", removal.eventDate);
        if (error) {
          return rejectWithValue(error.message);
        }
        plan = { deleteIds: (data ?? []).map((row) => row.id), updates: [] };
      }

      for (const update of plan.updates) {
        await dispatch(
          addBodyPhotos({ id: update.id, data: update.data, eventDate: update.eventDate, traineeId, userId }),
        ).unwrap();
      }
      if (plan.deleteIds.length) {
        await dispatch(deleteMeasurement({ ids: plan.deleteIds })).unwrap();
        const loadedPages = Math.max(1, getState().traineeMeasurements.photosCurrentPage);
        for (let page = 1; page <= loadedPages; page++) {
          await dispatch(fetchTraineeBodyPhotos({ traineeId, page: page === 1 ? undefined : page })).unwrap();
        }
      }
      analytics.track("remove_body_photos", { deletedSets: plan.deleteIds.length, shrunkSets: plan.updates.length });
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);
